import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getFirestore,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';

import { getFirebaseApp } from '../config/firebaseApp';
import type { ForumCategory, ForumCommentDoc, ForumPostDoc } from '../types/forum';

const COL = 'forum_posts';

function db() {
  return getFirestore(getFirebaseApp());
}

function mapPost(id: string, data: Record<string, unknown>): ForumPostDoc {
  const lb = data.likedBy;
  let likedBy: string[] = [];
  if (Array.isArray(lb)) {
    likedBy = lb.map(x => String(x ?? '')).filter(Boolean);
  }
  return {
    id,
    authorId: String(data.authorId ?? ''),
    isAnonymous: Boolean(data.isAnonymous),
    title: String(data.title ?? ''),
    content: String(data.content ?? ''),
    category: data.category as ForumPostDoc['category'],
    createdAt: data.createdAt as ForumPostDoc['createdAt'],
    likedBy,
    commentCount: Number(data.commentCount ?? 0),
  };
}

function mapComment(id: string, data: Record<string, unknown>): ForumCommentDoc {
  const lb = data.likedBy;
  let likedBy: string[] = [];
  if (Array.isArray(lb)) {
    likedBy = lb.map(x => String(x ?? '')).filter(Boolean);
  }
  return {
    id,
    authorId: String(data.authorId ?? ''),
    isAnonymous: Boolean(data.isAnonymous),
    content: String(data.content ?? ''),
    createdAt: data.createdAt as ForumCommentDoc['createdAt'],
    likedBy,
  };
}

export function subscribeForumPost(
  postId: string,
  onUpdate: (post: ForumPostDoc | null) => void,
  onError?: (e: Error) => void,
): () => void {
  return onSnapshot(
    doc(db(), COL, postId),
    snap => {
      if (!snap.exists()) {
        onUpdate(null);
        return;
      }
      onUpdate(mapPost(snap.id, snap.data() as Record<string, unknown>));
    },
    err => (onError ?? (() => {}))(err as Error),
  );
}

export function subscribeForumPosts(
  onUpdate: (posts: ForumPostDoc[]) => void,
  onError?: (e: Error) => void,
  max = 80,
): () => void {
  const q = query(
    collection(db(), COL),
    orderBy('createdAt', 'desc'),
    limit(max),
  );
  return onSnapshot(
    q,
    snap => {
      const list: ForumPostDoc[] = [];
      snap.forEach(d => {
        list.push(mapPost(d.id, d.data() as Record<string, unknown>));
      });
      onUpdate(list);
    },
    err => (onError ?? (() => {}))(err as Error),
  );
}

export function subscribeComments(
  postId: string,
  onUpdate: (comments: ForumCommentDoc[]) => void,
  onError?: (e: Error) => void,
): () => void {
  const q = query(
    collection(db(), COL, postId, 'comments'),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(
    q,
    snap => {
      const list: ForumCommentDoc[] = [];
      snap.forEach(d => {
        list.push(mapComment(d.id, d.data() as Record<string, unknown>));
      });
      onUpdate(list);
    },
    err => (onError ?? (() => {}))(err as Error),
  );
}

export async function createForumPost(input: {
  authorId: string;
  isAnonymous: boolean;
  title: string;
  content: string;
  category: ForumCategory;
}): Promise<string> {
  const ref = await addDoc(collection(db(), COL), {
    authorId: input.authorId,
    isAnonymous: input.isAnonymous,
    title: input.title.trim(),
    content: input.content.trim(),
    category: input.category,
    createdAt: serverTimestamp(),
    likedBy: [],
    commentCount: 0,
  });
  return ref.id;
}

export async function addComment(
  postId: string,
  authorId: string,
  content: string,
  isAnonymous: boolean,
): Promise<void> {
  const batch = writeBatch(db());
  const commentRef = doc(collection(db(), COL, postId, 'comments'));
  batch.set(commentRef, {
    authorId,
    isAnonymous,
    content: content.trim(),
    createdAt: serverTimestamp(),
    likedBy: [],
  });
  batch.update(doc(db(), COL, postId), {
    commentCount: increment(1),
  });
  await batch.commit();
}

/** Beğeniyi aç/kapa: UID listede ise çıkar, değilse ekle. */
export async function toggleLike(postId: string, uid: string): Promise<void> {
  const ref = doc(db(), COL, postId);
  await runTransaction(db(), async transaction => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) {
      throw new Error('Gönderi bulunamadı.');
    }
    const data = snap.data() as Record<string, unknown>;
    const raw = data.likedBy;
    const likedBy = Array.isArray(raw)
      ? raw.filter((x): x is string => typeof x === 'string')
      : [];
    if (likedBy.includes(uid)) {
      transaction.update(ref, { likedBy: arrayRemove(uid) });
    } else {
      transaction.update(ref, { likedBy: arrayUnion(uid) });
    }
  });
}

/** Yorum beğenisi: gönderi beğenisiyle aynı mantık. */
export async function toggleCommentLike(
  postId: string,
  commentId: string,
  uid: string,
): Promise<void> {
  const ref = doc(db(), COL, postId, 'comments', commentId);
  await runTransaction(db(), async transaction => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) {
      throw new Error('Yorum bulunamadı.');
    }
    const data = snap.data() as Record<string, unknown>;
    const raw = data.likedBy;
    const likedBy = Array.isArray(raw)
      ? raw.filter((x): x is string => typeof x === 'string')
      : [];
    if (likedBy.includes(uid)) {
      transaction.update(ref, { likedBy: arrayRemove(uid) });
    } else {
      transaction.update(ref, { likedBy: arrayUnion(uid) });
    }
  });
}

export async function updateForumPost(
  postId: string,
  authorId: string,
  input: { title: string; content: string },
): Promise<void> {
  const ref = doc(db(), COL, postId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error('Gönderi bulunamadı.');
  }
  if (String(snap.data()?.authorId ?? '') !== authorId) {
    throw new Error('Bu gönderiyi düzenleyemezsin.');
  }
  await updateDoc(ref, {
    title: input.title.trim(),
    content: input.content.trim(),
  });
}
